import { useState, useCallback } from 'react'

import {
  generateEIP712SigningPayload,
  validateEIP712Signature,
  generateEIP1559SigningPayload,
  validateEIP1559Signature,
  generateSolanaSigningPayload,
  validateSolanaSignature,
} from '@features/cpn.script/lib'

import type {
  ValidationResult,
  EIP712SigningPayload,
  EIP1559SigningPayload,
  SolanaSigningPayload,
  TestnetChain,
} from '@features/cpn.script/lib'

// Hook-specific types
interface ParameterForm {
  chain: string
  walletAddress: string
}

interface SignatureForm {
  signature: string
}

export type SigningMethod = 'eip712' | 'eip1559' | 'solana'
export type Step = 'method' | 'parameters' | 'signature' | 'results'

export interface GeneratedPayload {
  method: SigningMethod
  payload: EIP712SigningPayload | EIP1559SigningPayload | SolanaSigningPayload
  formattedPayload: string
}

/**
 * Custom hook for managing wallet compatibility validator state and operations.
 */
export const useWalletValidator = () => {
  const [currentStep, setCurrentStep] = useState<Step>('method')
  const [selectedMethod, setSelectedMethod] = useState<SigningMethod | null>(
    null,
  )
  const [generatedPayload, setGeneratedPayload] =
    useState<GeneratedPayload | null>(null)
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null)
  const [isValidating, setIsValidating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Handle method selection
  const handleMethodSelection = useCallback((method: SigningMethod) => {
    setSelectedMethod(method)
    setCurrentStep('parameters')

    // Reset subsequent steps
    setGeneratedPayload(null)
    setValidationResult(null)
    setError(null)
  }, [])

  // Handle payload generation
  const handlePayloadGeneration = useCallback(
    (data: ParameterForm) => {
      if (!selectedMethod) return

      setError(null) // Clear any previous errors
      try {
        // Validate inputs before proceeding
        if (!data.chain || !data.walletAddress) {
          throw new Error('Chain and wallet address are required')
        }

        const input = {
          chain: data.chain as TestnetChain,
          walletAddress: data.walletAddress,
        }

        let payload:
          | EIP712SigningPayload
          | EIP1559SigningPayload
          | SolanaSigningPayload
        let formattedPayload: string

        switch (selectedMethod) {
          case 'eip712': {
            const eip712Payload = generateEIP712SigningPayload(input)
            payload = eip712Payload
            formattedPayload = JSON.stringify(eip712Payload.payload, null, 2)
            break
          }
          case 'eip1559': {
            const eip1559Payload = generateEIP1559SigningPayload(input)
            payload = eip1559Payload
            formattedPayload = JSON.stringify(eip1559Payload.payload, null, 2)
            break
          }
          case 'solana': {
            const solanaPayload = generateSolanaSigningPayload(input)
            payload = solanaPayload
            formattedPayload = JSON.stringify(solanaPayload.payload, null, 2)
            break
          }
        }

        setGeneratedPayload({
          method: selectedMethod,
          payload,
          formattedPayload,
        })
        setCurrentStep('signature')
      } catch (error) {
        setError(
          `Failed to generate payload: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        )
      }
    },
    [selectedMethod],
  )

  // Handle signature validation
  const handleSignatureValidation = useCallback(
    async (data: SignatureForm) => {
      if (!generatedPayload) return

      setIsValidating(true)
      try {
        let result: ValidationResult

        switch (generatedPayload.method) {
          case 'eip712':
            result = await validateEIP712Signature(
              generatedPayload.payload as EIP712SigningPayload,
              { signature: data.signature },
            )
            break
          case 'eip1559':
            result = await validateEIP1559Signature(
              generatedPayload.payload as EIP1559SigningPayload,
              { signature: data.signature },
            )
            break
          case 'solana':
            result = validateSolanaSignature(
              generatedPayload.payload as SolanaSigningPayload,
              { signature: data.signature },
            )
            break
        }

        setValidationResult(result)
        setCurrentStep('results')
      } catch (error) {
        setValidationResult({
          success: false,
          message: 'Validation failed',
          details: error instanceof Error ? error.message : 'Unknown error',
        })
        setCurrentStep('results')
      } finally {
        setIsValidating(false)
      }
    },
    [generatedPayload],
  )

  // Navigation handlers
  const handleBackToMethod = useCallback(() => {
    setCurrentStep('method')
  }, [])

  const handleBackToParameters = useCallback(() => {
    setCurrentStep('parameters')
  }, [])

  const handleBackToSignature = useCallback(() => {
    setCurrentStep('signature')
  }, [])

  // Reset to start
  const resetFlow = useCallback(() => {
    setCurrentStep('method')
    setSelectedMethod(null)
    setGeneratedPayload(null)
    setValidationResult(null)
    setError(null)
  }, [])

  return {
    // State
    currentStep,
    selectedMethod,
    generatedPayload,
    validationResult,
    isValidating,
    error,

    // Handlers
    handleMethodSelection,
    handlePayloadGeneration,
    handleSignatureValidation,
    handleBackToMethod,
    handleBackToParameters,
    handleBackToSignature,
    resetFlow,
  }
}
